import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TeacherFormDialog from "@/components/TeacherFormDialog";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Eye, UserSquare, Mail, Phone, KeyRound } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  attivo:   "bg-emerald-50 text-emerald-800 border-emerald-200",
  sospeso:  "bg-orange-50 text-orange-800 border-orange-200",
  cessato:  "bg-stone-200 text-stone-700 border-stone-300",
};
const STATUS_LABEL = { attivo: "Attivo", sospeso: "Sospeso", cessato: "Cessato" };

export default function Teachers({ track }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTeacher, setEditTeacher] = useState(null);
  const [delTeacher, setDelTeacher] = useState(null);
  const [activated, setActivated] = useState(null); // { email, temp_password }

  const { data: teachers = [], isLoading } = useQuery({
    queryKey: ["teachers", { search, statusFilter, track }],
    queryFn: async () => {
      const params = {};
      if (track) params.track = track;
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;
      return (await api.get("/teachers", { params })).data;
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["teachers-stats"],
    queryFn: async () => (await api.get("/teachers-stats/overview")).data,
  });

  const createMut = useMutation({
    mutationFn: async (p) => (await api.post("/teachers", p)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["teachers"] });
      qc.invalidateQueries({ queryKey: ["teachers-stats"] });
      toast.success("Docente creato");
      setCreateOpen(false);
      navigate(`/scuola/docenti/${d.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const editMut = useMutation({
    mutationFn: async ({ id, p }) => (await api.patch(`/teachers/${id}`, p)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teachers"] });
      toast.success("Docente aggiornato");
      setEditTeacher(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/teachers/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teachers"] });
      qc.invalidateQueries({ queryKey: ["teachers-stats"] });
      toast.success("Docente eliminato");
      setDelTeacher(null);
    },
    onError: (e) => { toast.error(e.response?.data?.detail || "Errore"); setDelTeacher(null); },
  });

  const activateMut = useMutation({
    mutationFn: async (id) => (await api.post("/teacher-accounts/activate", { teacher_id: id, send_email: true })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["teachers"] });
      toast.success(`Account attivato per ${d.email}`);
      setActivated(d);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore attivazione"),
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title={track === "modulare" ? "Docenti · Scuola Modulare" : "Docenti"}
        subtitle="Anagrafica docenti, materie insegnate, CV e materiale didattico."
        actions={
          <Button
            data-testid="teacher-add-btn"
            onClick={() => setCreateOpen(true)}
            className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]"
          >
            <Plus size={16} className="mr-1.5"/> Nuovo docente
          </Button>
        }
      />

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><UserSquare size={12}/> Totale</div>
          <div className="text-2xl font-semibold mt-1">{stats?.total || 0}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">Attivi</div>
          <div className="text-2xl font-semibold mt-1 text-emerald-700">{stats?.active || 0}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Coordinatori d&apos;area</div>
          <div className="text-2xl font-semibold mt-1">{stats?.coordinators || 0}</div>
        </Card>
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 flex flex-col md:flex-row md:items-center gap-3 border-b border-stone-100">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input
              data-testid="teacher-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, email, materia…"
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="teacher-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="attivo">Attivi</SelectItem>
              <SelectItem value="sospeso">Sospesi</SelectItem>
              <SelectItem value="cessato">Cessati</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="p-4">
          {isLoading ? (
            <div className="py-10 text-center text-stone-400 text-sm">Caricamento…</div>
          ) : teachers.length === 0 ? (
            <div className="py-14 text-center text-stone-400 text-sm">
              Nessun docente. Crea il primo con &laquo;Nuovo docente&raquo;.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teachers.map((t, i) => (
                <Card
                  key={t.id}
                  data-testid={`teacher-card-${t.id}`}
                  className="p-4 border border-stone-200 rounded-xl shadow-none hover:border-[#1C352D] transition-colors duration-150 animate-in-row"
                  style={{ animationDelay: `${i * 20}ms` }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-full bg-[#1C352D] text-white grid place-items-center text-sm font-semibold shrink-0">
                      {(t.first_name?.[0] || "?").toUpperCase()}{(t.last_name?.[0] || "").toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Link to={`/scuola/docenti/${t.id}`} className="font-medium truncate hover:text-[#1C352D]">
                          {t.academic_title ? `${t.academic_title} ` : ""}{t.first_name} {t.last_name}
                        </Link>
                        <Badge className={`text-[10px] rounded-full border ${STATUS_STYLES[t.status] || STATUS_STYLES.attivo}`}>
                          {STATUS_LABEL[t.status] || t.status}
                        </Badge>
                      </div>
                      <div className="flex gap-1 mt-1" data-testid={`teacher-tracks-${t.id}`}>
                        {(t.tracks?.length ? t.tracks : ["residenziale"]).map((tr) => (
                          <span key={tr} className={`text-[10px] px-2 py-0.5 rounded-full border ${tr === "modulare" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
                            {tr === "modulare" ? "Modulare" : "Residenziale"}
                          </span>
                        ))}
                      </div>
                      <div className="text-[11px] text-stone-500 mt-0.5 truncate">{t.email || "—"}</div>
                      {t.subjects?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {t.subjects.slice(0, 4).map((s, idx) => (
                            <span key={idx} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{s}</span>
                          ))}
                          {t.subjects.length > 4 && <span className="text-[10px] text-stone-400">+{t.subjects.length - 4}</span>}
                        </div>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`teacher-actions-${t.id}`} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-[#1C352D]">
                          <MoreHorizontal size={16}/>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48 bg-white">
                        <DropdownMenuItem onClick={() => navigate(`/scuola/docenti/${t.id}`)}>
                          <Eye size={14} className="mr-2"/> Apri scheda
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditTeacher(t)}>
                          <Pencil size={14} className="mr-2"/> Modifica
                        </DropdownMenuItem>
                        {!t.account_activated && t.email && (
                          <DropdownMenuItem
                            data-testid={`teacher-activate-${t.id}`}
                            onClick={() => activateMut.mutate(t.id)}
                          >
                            <KeyRound size={14} className="mr-2"/> Attiva account portale
                          </DropdownMenuItem>
                        )}
                        {t.account_activated && (
                          <DropdownMenuItem disabled>
                            <KeyRound size={14} className="mr-2"/> Account attivato ✓
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator/>
                        <DropdownMenuItem
                          className="text-red-700 focus:text-red-800 focus:bg-red-50"
                          onClick={() => setDelTeacher(t)}
                        >
                          <Trash2 size={14} className="mr-2"/> Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-[11px] text-stone-500">
                    {t.phone && <span className="inline-flex items-center gap-1"><Phone size={11}/> {t.phone}</span>}
                    {t.city && <span>{t.city}</span>}
                    {t.email && <span className="inline-flex items-center gap-1 truncate"><Mail size={11}/> {t.email}</span>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>

      <TeacherFormDialog
        open={createOpen} onOpenChange={setCreateOpen} mode="create" defaultTrack={track || "residenziale"}
        submitting={createMut.isPending}
        onSubmit={(p) => createMut.mutate(p)}
      />
      <TeacherFormDialog
        open={!!editTeacher} onOpenChange={(v) => { if (!v) setEditTeacher(null); }}
        mode="edit" initial={editTeacher}
        submitting={editMut.isPending}
        onSubmit={(p) => editMut.mutate({ id: editTeacher.id, p })}
      />

      <AlertDialog open={!!delTeacher} onOpenChange={(v) => { if (!v) setDelTeacher(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {delTeacher ? `${delTeacher.first_name} ${delTeacher.last_name}` : "questo docente"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Il docente verrà rimosso e scollegato da corsi/moduli. I materiali resteranno archiviati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => delTeacher && delMut.mutate(delTeacher.id)}
              data-testid="teacher-delete-confirm"
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!activated} onOpenChange={(v) => { if (!v) setActivated(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Account portale attivato</AlertDialogTitle>
            <AlertDialogDescription>
              Il docente riceverà un&apos;email con le credenziali. Copia queste dettagli come backup:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="p-4 bg-stone-50 rounded-lg space-y-1.5 text-sm">
            <div><b>Email:</b> <span className="mono">{activated?.email}</span></div>
            <div><b>Password provvisoria:</b> <code className="bg-white px-2 py-0.5 rounded border border-stone-200 mono">{activated?.temp_password}</code></div>
            <div className="text-[11px] text-stone-500 mt-2">Il docente dovrà impostare una nuova password al primo accesso.</div>
          </div>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="teacher-activated-ok" onClick={() => setActivated(null)} className="bg-[#1C352D] hover:bg-[#264A3E]">Ho capito</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
